/**
 * Exercice « sur la feuille » — les propositions du QCM en touches de clavier.
 *
 * Une touche porte sa lettre et, quand c'est court, le libellé (« A  12 cm »).
 * Dès qu'une proposition est longue, a une figure, ou qu'il y en a trop, toutes
 * les touches passent à la lettre seule et les propositions s'écrivent sur la
 * feuille, sous la question (« A. … B. … »). Règle pure, testée.
 */

export interface KeyOption {
  id: string;
  /** libellé en markdown + LaTeX */
  label: string;
  /** la proposition a une figure */
  hasVisuals: boolean;
}

export type KeyLayout = 'labels' | 'letters';

/** Au-delà, une touche ne garde que sa lettre. */
export const KEY_LABEL_MAX = 14;
/** Au-delà de ce nombre de propositions, toujours la lettre seule. */
export const KEY_LABEL_MAX_OPTIONS = 6;

const LATEX_SHORT: Record<string, string> = {
  times: '×',
  div: '÷',
  cdot: '·',
  pi: 'π',
  infty: '∞',
  leq: '≤',
  geq: '≥',
  le: '≤',
  ge: '≥',
  neq: '≠',
  approx: '≈',
  pm: '±',
  sqrt: '√',
  degree: '°',
  circ: '°',
  ldots: '…',
  dots: '…',
};

/**
 * Longueur LUE d'un libellé (approximative) : `$`, accolades, `^`/`_` et noms
 * de commandes LaTeX retirés ; `\frac{a}{b}` compte « a/b ».
 */
export function visibleLength(md: string): number {
  let s = md.replace(/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, '$1/$2');
  s = s.replace(/\\(left|right|mathrm|text|textbf|operatorname|displaystyle|,|;|!|quad)/g, '');
  s = s.replace(/\\([a-zA-Z]+)/g, (_, name: string) => LATEX_SHORT[name] ?? name.slice(0, 1));
  s = s.replace(/[${}^_*`]/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return Array.from(s).length;
}

export function choiceKeyLayout(options: readonly KeyOption[], max = KEY_LABEL_MAX): KeyLayout {
  if (options.length === 0) return 'labels';
  if (options.length > KEY_LABEL_MAX_OPTIONS) return 'letters';
  for (const o of options) {
    if (o.hasVisuals) return 'letters';
    if (/\n|\|/.test(o.label.trim())) return 'letters'; // liste, tableau
    if (visibleLength(o.label) > max) return 'letters';
  }
  return 'labels';
}

export function keyLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** Touche du clavier physique → index de la proposition (1-9 ou A-Z), sinon null. */
export function keyboardChoiceIndex(key: string, count: number): number | null {
  let i: number | null = null;
  if (/^[1-9]$/.test(key)) i = Number(key) - 1;
  else if (/^[a-zA-Z]$/.test(key)) i = key.toUpperCase().charCodeAt(0) - 65;
  return i !== null && i < count ? i : null;
}
